import { useEffect, useRef, useState } from 'react';
import { API_ORIGIN } from './config';

/**
 * Live server events, for the three streams the backend has always served and
 * the app replaced with a timer.
 *
 * Packaging, company analysis and bucket listing each publish SSE. This page
 * polled them instead - the analysis history every eight seconds, the sync
 * history every ten - so a run that finished in under a second still looked
 * frozen until the next tick, and a tool that completed mid-run never appeared
 * at all. That is why runs lurched rather than moved.
 *
 * The stream drives cache invalidation rather than rendering state of its own:
 * an event means "the truth changed, read it again", so every number on screen
 * still comes from the route that owns it. Polling stays as the fallback, so a
 * stream that never connects degrades to exactly today's behaviour instead of
 * a page that stops updating.
 *
 * Event names are the backend's own, read from the Rust:
 *   analysis   run-queued, run-started, tool-started, tool-recovered,
 *              tool-completed, run-completed, stage-entered
 *   sync       registered, transition, progress, terminal
 *   packaging  packaging
 * plus the shared failure frames dependency-error and internal-error.
 */

export type StreamState = 'connecting' | 'live' | 'closed' | 'error';

export const ANALYSIS_EVENTS = [
  'run-queued',
  'run-started',
  'tool-started',
  'tool-recovered',
  'tool-completed',
  'run-completed',
  'stage-entered',
] as const;

export const SYNC_EVENTS = ['registered', 'transition', 'progress', 'terminal'] as const;

export const PACKAGING_EVENTS = ['packaging'] as const;

/**
 * The server's own admission that history was lost.
 *
 * A stream that resumes past the retention contract emits this instead of the
 * events it can no longer produce. Not listening for it is how a shorter
 * stream gets presented as a complete one, which the workflow contract forbids
 * in as many words. It is named separately from the per-surface lists because
 * every stream can emit it and none of them own it.
 */
export const RETENTION_GAP_EVENT = 'retention-gap';

/** Events after which the server closes and there is nothing more to hear. */
const TERMINAL = new Set(['run-completed', 'terminal']);

/**
 * Whether a frame's own payload says the run has ended.
 *
 * Analysis and sync end by frame NAME, and packaging does not: it emits one
 * name, `packaging`, and carries its terminality inside the body as
 * `kind: TERMINAL`. So the name-only test could never be true for packaging,
 * `terminal` never got set, and when the server closed after a run finished
 * EventSource reconnected to a closed stream every few seconds for as long as
 * the tab stayed open.
 */
function isTerminalPayload(data: unknown): boolean {
  return typeof data === 'object' && data !== null && (data as { kind?: unknown }).kind === 'TERMINAL';
}

/**
 * The failure frames a server sends, as opposed to the one the browser raises.
 *
 * The packaging stream names its failure frame `error`, which is also the name
 * EventSource gives its own transport event. They mean opposite things: the
 * first is the server reporting the run failed, the second is a dropped
 * connection the browser is already retrying.
 */
export const FAILURE_EVENTS = ['dependency-error', 'internal-error', 'error'] as const;

/**
 * Whether an `error` event came from the server or from the transport.
 *
 * A named SSE frame is delivered as a MessageEvent and carries `data`, even
 * when that data is an empty string. EventSource's own error event is a plain
 * Event with no such property. The name cannot tell them apart, so this does.
 *
 * Getting this wrong is not cosmetic: closing the source on a transport error
 * ends the browser's automatic reconnection, and the stream never comes back
 * without a remount.
 */
export function isServerFailureFrame(event: Event): boolean {
  return 'data' in event;
}

export function analysisStreamUrl(runId: string): string {
  return `${API_ORIGIN}/api/v1/company-analysis-runs/${encodeURIComponent(runId)}/stream`;
}

export function syncStreamUrl(syncRunId: string): string {
  return `${API_ORIGIN}/api/v1/sync-runs/${encodeURIComponent(syncRunId)}/stream`;
}

export function packagingStreamUrl(runId: string): string {
  return `${API_ORIGIN}/api/v1/packaging-runs/${encodeURIComponent(runId)}/stream`;
}

/**
 * Subscribes while `url` is set, calling `onEvent` for each named frame.
 *
 * `onEvent` is held in a ref so a caller can pass an inline closure without
 * tearing the connection down on every render - a reconnect loop against a
 * streaming endpoint is worse than the polling it replaced.
 */
export function useLiveStream(
  url: string | null,
  events: readonly string[],
  onEvent: (name: string, data: unknown) => void,
): StreamState {
  const [state, setState] = useState<StreamState>('connecting');
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    if (!url) {
      setState('closed');
      return;
    }
    setState('connecting');
    const source = new EventSource(url, { withCredentials: true });
    let terminal = false;

    const listen = (name: string) => {
      source.addEventListener(name, (e) => {
        setState('live');
        let data: unknown = null;
        try {
          data = JSON.parse((e as MessageEvent).data as string);
        } catch {
          // A frame we cannot parse is still a signal that something changed;
          // the caller re-reads the route either way.
        }
        handler.current(name, data);
        if (TERMINAL.has(name) || isTerminalPayload(data)) {
          terminal = true;
          source.close();
          setState('closed');
        }
      });
    };
    events.forEach(listen);
    // Every stream can emit this and no caller has to opt in, because a lost
    // history is never something a surface chooses not to hear about.
    listen(RETENTION_GAP_EVENT);

    // Listening for none of these left a failed run reporting live while
    // frozen. Listening for all of them by name closed the connection on the
    // browser's own transport error, which shares the name `error` and is the
    // opposite event: a retry in progress rather than a run that died.
    /**
     * A transport event means one of two things, and only `readyState` says
     * which: CONNECTING is the browser retrying, CLOSED is the browser giving
     * up. Reporting both as a retry is the mirror image of the bug this
     * replaced, and the more dangerous half, because the stale claim is the
     * reassuring one: a stream that is never coming back sits on
     * "reconnecting" forever.
     */
    const transportFailed = () => {
      if (terminal) return;
      if (source.readyState === EventSource.CLOSED) {
        setState('error');
        return;
      }
      setState((s) => (s === 'live' ? 'connecting' : s));
    };

    for (const failure of FAILURE_EVENTS) {
      source.addEventListener(failure, (e) => {
        if (!isServerFailureFrame(e)) {
          transportFailed();
          return;
        }
        terminal = true;
        // A failure is a change in the truth, so the caller re-reads the route
        // that owns it. Without this the run row kept rendering RUNNING from
        // the last good read while the stream knew the run had died.
        let data: unknown = null;
        try {
          data = JSON.parse((e as MessageEvent).data as string);
        } catch {
          // An unparseable failure frame is still a failure.
        }
        handler.current(failure, data);
        setState('error');
        source.close();
      });
    }

    source.onerror = transportFailed;

    return () => source.close();
  }, [url, events]);

  return state;
}
